import { FC } from "react";
import Logo from "shared/Logo/Logo";
import { NavSlot, useNavFit } from "shared/Navigation/NavFit";
import MenuBar from "shared/MenuBar/MenuBar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useLocation } from "react-router-dom";
import { useData } from "data/data-provider";
import TopBarSearch from "designs/Common/TopBarSearch/TopBarSearch";
import { useTopBarSearchVisible } from "designs/Common/TopBarSearch/useTopBarSearchVisible";

export interface MainNav1Props {
  className?: string;
  /** The copy pinned to the top once the header has scrolled away (PinnedHeader, 1NOW-11153). */
  pinned?: boolean;
}

// Header layout variants, chosen in the FMS website builder and stored on
// CompanyWebsiteDetail.header_layout:
//   "classic"      → logo left, nav right, "Book Now" CTA pinned right; default
//   "split_cta"    → logo left, nav centered, "Book Now" CTA pinned right
//   "drawer_right" → logo left, "Book Now" CTA + menu button right; nav lives
//                    in the slide-out panel from the right (no inline desktop nav)
//
// The "Book Now" CTA is the primary conversion action and is shown in every
// layout (1NOW-1240) — only its placement and the nav arrangement differ.
type HeaderVariant = "classic" | "split_cta" | "drawer_right";

const MainNav: FC<MainNav1Props> = ({ className = "", pinned = false }) => {
  // `!` on the show/hide variants below: see shared/Navigation/Navigation.tsx
  // (Meta Event Setup Tool injects a competing Tailwind sheet).
  const location = useLocation();
  const { ourCompany, companyDetails } = useData();
  // 1NOW-11153: the search bar in the top bar. It takes the nav's place on
  // desktop, so the menu button shows at every width while it is up.
  // On the home page the header above the banner keeps its menu; the pinned copy carries the
  // search, as on Design B.
  const searchVisible = useTopBarSearchVisible();
  const showSearch = searchVisible && (pinned || !["/", "/home"].includes(location.pathname));
  // 1NOW-11061: the inline nav (classic, split_cta) sits in a slot between the logo and the
  // right cluster and is shown only while it fits; otherwise the menu button lists every item.
  // The nav never overlaps the logo or Book Now, however many pages the host adds.
  const fit = useNavFit("lg");

  // Source of truth for the layout is the FMS-set `header_layout`. A
  // `?headerVariant=` query param overrides it for live preview (handy
  // before the tenant has saved a choice). Defaults to "classic" so every
  // existing tenant keeps the original Design A bar untouched.
  const params = new URLSearchParams(location.search);
  const variant: HeaderVariant =
    (params.get("headerVariant") as HeaderVariant) ||
    (companyDetails?.header_layout as HeaderVariant) ||
    "classic";

  if (variant === "drawer_right") {
    /* ─────────────────────────────────────────────────────────────
       Drawer right — logo left · Book Now CTA + menu button right. The
       existing MenuBar trigger is forced visible at ALL breakpoints, so
       the nav lives entirely in the slide-out panel instead of inline;
       the Book Now CTA stays in the bar (from sm up) so the primary
       conversion action is reachable without opening the drawer.
       ───────────────────────────────────────────────────────────── */
    return (
      <div className={`nc-MainNav1 relative z-10 ${className}`}>
        <div
          className={`px-4 lg:container ${ourCompany?.company?.id === 1592 ? "py-0" : "py-2"} relative flex justify-between items-center ${showSearch ? "gap-2 sm:gap-4" : ""}`}
        >
          {/* The brand. While the search bar is up it stays, left of it and size-capped (Logo
              compact), at every width, as on Design B (1NOW-11153 reopen, 8 Oct). */}
          <div className="flex flex-shrink-0 justify-start items-center space-x-4 sm:space-x-10">
            <Logo compact={showSearch} />
          </div>

          {showSearch && <TopBarSearch logo={<Logo />} compact />}

          <div className="flex items-center space-x-4">
            <ButtonPrimary
              href="/vehicles"
              className={showSearch ? "hidden" : "hidden sm:!inline-flex"}
              sizeClass="px-5 py-2.5"
            >
              Book Now
            </ButtonPrimary>
            <MenuBar />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "split_cta") {
    /* ─────────────────────────────────────────────────────────────
       Split + CTA — logo left · nav centred · Book Now right.
       The nav is centred in the flex slot between the logo and the CTA, not
       pinned to the bar (it used to be absolute at left-1/2, which slid it
       under the logo and the CTA once a host added many pages, 1NOW-11061).
       Below lg, or when it does not fit the slot, the nav collapses into the
       hamburger (same as classic) — the nav links only appear at lg
       (Navigation is `hidden lg:!flex`), so the menu trigger MUST mirror
       that with `lg:hidden`. Using `md:hidden` here left a 768–1023px
       dead zone (iPad Air) where neither the nav nor the hamburger
       showed (1NOW-1238). The CTA stays visible from sm up. */
    return (
      <div className={`nc-MainNav1 relative z-10 ${className}`}>
        <div
          className={`px-4 lg:container ${ourCompany?.company?.id === 1592 ? "py-0" : "py-2"} relative flex justify-between items-center ${showSearch ? "gap-2 sm:gap-4" : ""}`}
        >
          {/* The brand. While the search bar is up it stays, left of it and size-capped (Logo
              compact), at every width, as on Design B (1NOW-11153 reopen, 8 Oct). */}
          <div className="flex flex-shrink-0 justify-start items-center space-x-4 sm:space-x-10">
            <Logo compact={showSearch} />
          </div>

          {showSearch ? <TopBarSearch logo={<Logo />} compact /> : <NavSlot fit={fit} align="center" className="mx-4" />}

          <div className="flex items-center space-x-4">
            <ButtonPrimary
              href="/vehicles"
              className={showSearch ? "hidden" : "hidden sm:!inline-flex"}
              sizeClass="px-5 py-2.5"
            >
              Book Now
            </ButtonPrimary>
            <div
              ref={fit.menuRef}
              className={`flex ${showSearch ? "" : fit.menuClass} items-center`}
            >
              <div className="px-0.5" />
              <MenuBar />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────────────
     Classic (default) — logo left · nav + Book Now CTA right. This is
     what every live tenant (header_layout=null) renders. The desktop
     nav, the persistent Book Now CTA (from sm up) and the mobile menu
     trigger share one right-side cluster so the CTA sits beside the nav
     on desktop and beside the hamburger on mobile (1NOW-1240).
     ─────────────────────────────────────────────────────────────── */
  return (
    <div className={`nc-MainNav1 relative z-10 ${className}`}>
      <div
        className={`px-4 lg:container ${ourCompany?.company?.id === 1592 ? "py-0" : "py-2"} relative flex justify-between items-center ${showSearch ? "gap-2 sm:gap-4" : ""}`}
      >
        {/* The brand; a size-capped copy while the search bar is up (1NOW-11153 reopen, 8 Oct) */}
        <div className="flex flex-shrink-0 justify-start items-center space-x-4 sm:space-x-10">
          <Logo compact={showSearch} />
        </div>

        {showSearch && <TopBarSearch logo={<Logo />} compact />}

        {/* Desktop nav, right-aligned beside the CTA while it fits (1NOW-11061) */}
        {!showSearch && <NavSlot fit={fit} align="end" className="mx-4" />}

        <div className="flex items-center space-x-4">
          <ButtonPrimary
            href="/vehicles"
            className={showSearch ? "hidden" : "hidden sm:!inline-flex"}
            sizeClass="px-5 py-2.5"
          >
            Book Now
          </ButtonPrimary>
          <div
            ref={fit.menuRef}
            className={`flex ${showSearch ? "" : fit.menuClass} items-center`}
          >
            <MenuBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainNav;
